import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { Printer } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";

export default function Handout() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [event, setEvent] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    Promise.all([api.get(`/events/${slug}`), api.get("/admin/promos")])
      .then(([eventResponse, promoResponse]) => {
        setEvent(eventResponse.data);
        const active = promoResponse.data.filter((item) => item.is_active);
        setPromo(active.find((item) => item.event_id === eventResponse.data.id) || active.find((item) => !item.event_id) || null);
      })
      .catch(() => setEvent(false));
  }, [slug, user]);

  if (loading) return <div data-testid="handout-loading-state" className="min-h-screen bg-onyx pt-40 text-center font-mono text-xs uppercase tracking-[0.3em] text-gold">Loading</div>;
  if (!user || user.role !== "admin") return <Navigate to="/account" replace />;
  if (event === false) return <div data-testid="handout-not-found" className="flex min-h-screen items-center justify-center bg-onyx text-zinc-400">Event not found.</div>;
  if (!event) return <div className="min-h-screen bg-onyx pt-40 text-center font-mono text-xs uppercase tracking-[0.3em] text-gold">Preparing card</div>;

  const galleryUrl = `${window.location.origin}/events/${event.slug}`;
  const promoLabel = promo ? (promo.kind === "percent" ? `${Number(promo.value)}% off` : `$${Number(promo.value)} off`) : null;

  return (
    <div data-testid="handout-page" className="flex min-h-screen flex-col items-center bg-onyx px-4 pb-24 pt-32">
      <Seo title={`Handout — ${event.title}`} description={`Printable attendee QR card for ${event.title}.`} />
      <div className="mb-8 flex items-center gap-4 print:hidden">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">Print preview · letter paper recommended</p>
        <button data-testid="handout-print-button" onClick={() => window.print()} className="inline-flex items-center gap-2 bg-gold px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] text-onyx transition-colors hover:bg-titanium">
          <Printer className="h-4 w-4" /> Print card
        </button>
      </div>

      <div id="handout-card" data-testid="handout-card" className="w-full max-w-md border border-gold/40 bg-carbon p-10 text-center" style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
        <p className="font-mono text-[10px] uppercase tracking-[0.5em] text-gold">mellowjmedia</p>
        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Your photos are ready</p>
        <h1 className="mt-3 font-display text-4xl font-light text-white">{event.title}</h1>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-titanium/70">{event.date} · {event.location}</p>

        <div data-testid="handout-qr" className="mx-auto mt-8 w-fit border border-gold/30 bg-white p-4">
          <QRCode value={galleryUrl} size={200} bgColor="#ffffff" fgColor="#070708" />
        </div>
        <p className="mt-6 text-sm leading-6 text-zinc-400">Scan to browse watermarked previews and buy clean high-resolution downloads from this event.</p>

        {promo ? (
          <div className="mt-8 border border-gold/40 bg-gold/10 px-6 py-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">Attendee offer · {promoLabel}</p>
            <p data-testid="handout-promo-code" className="mt-2 font-mono text-3xl font-semibold uppercase tracking-[0.34em] text-gold">{promo.code}</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Enter at checkout{promo.max_redemptions ? ` · first ${promo.max_redemptions} redemptions` : ""}</p>
          </div>
        ) : (
          <p data-testid="handout-no-promo" className="mt-8 border border-white/10 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">No active promo code for this event</p>
        )}

        <p className="mt-8 break-all font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-600">{galleryUrl}</p>
        <div className="mx-auto mt-6 h-px w-16 bg-gold/40" />
        <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-600">Cinematic event photography · Digital delivery</p>
      </div>
    </div>
  );
}
